import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wheel } from '../components/Wheel'
import { ContextInputModal } from '../components/ContextInputModal'
import { useWheelStore } from '../stores/wheelStore'
import { Helmet } from 'react-helmet-async'






export const WheelPage: React.FC = () => {
  const navigate = useNavigate()
  const [isContextModalOpen, setIsContextModalOpen] = useState(false)

  const prevSpinning = useRef(false)
  

  


  const {
    options,
    isSpinning,
    result,
    addOption,
    removeOption,

    spin,
    spinEnd,
    mode,
    setMode,
    pendingElimination,
    accumulationCounts,
    soundEnabled,

  } = useWheelStore()

  useEffect(() => {
    useWheelStore.setState({
      options: [
        { id: '1', text: 'YES' },
        { id: '2', text: 'NO' },
      ],
      settings: {
        speed: 5,
        duration: 3,
        theme: 'default',
        soundEnabled: true,
        confettiEnabled: true,
      },
      isSpinning: false,
      result: null,
      history: []
    })
  }, [])

  useEffect(() => {
    prevSpinning.current = isSpinning
  }, [isSpinning])

  // Handle anchor scrolling when page loads
  useEffect(() => {
    const hash = window.location.hash
    if (hash === '#decision-wheel') {
      const element = document.getElementById('decision-wheel')
      if (element) {
        // Add a small delay to ensure the page is fully loaded
        setTimeout(() => {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          })
        }, 100)
      }
    }
  }, [])





  const handleSpin = () => {
    if (soundEnabled) {
      const audio = new Audio('/audio/spin.wav')
      audio.play().catch(e => console.warn('Audio play failed:', e))
    }
    spin()
  }

  return (
    <>
      <Helmet>
        <title>AI Decision Maker - Make Better Decisions with AI</title>
        <meta name="description" content="Get instant AI analysis for any decision. Spin the wheel and get smart insights in 3 seconds. Free to use, no registration required." />
        <meta name="keywords" content="decision maker, AI analysis, decision wheel, pros and cons, smart choices, free decision tool" />
        <meta property="og:title" content="AI Decision Maker - Make Better Decisions with AI" />
        <meta property="og:description" content="Get instant AI analysis for any decision. Spin the wheel and get smart insights in 3 seconds." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aidecision.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Decision Maker - Make Better Decisions with AI" />
        <meta name="twitter:description" content="Get instant AI analysis for any decision. Spin the wheel and get smart insights in 3 seconds." />
        <meta name="twitter:image" content="https://aidecision.com/og-image.jpg" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "AI Decision Maker",
            "description": "Get instant AI analysis for any decision. Spin the wheel and get smart insights in 3 seconds.",
            "url": "https://aidecision.com",
            "applicationCategory": "ProductivityApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "AI Decision Analysis",
              "Decision Wheel",
              "Pros and Cons Analysis",
              "Instant Results"
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold text-primary-700 mb-6">
              Make Better Decisions with AI
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              Spin the wheel and get instant AI analysis. No more guessing, no more regrets.
            </p>
            
            {/* Quick Benefits */}
            <div className="mb-12 max-w-3xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-2xl mb-2">⚡</div>
                    <div className="font-semibold text-gray-900">3-Second Analysis</div>
                    <div className="text-sm text-gray-600">Instant AI insights</div>
                  </div>
                  <div>
                    <div className="text-2xl mb-2">🎯</div>
                    <div className="font-semibold text-gray-900">Smart Recommendations</div>
                    <div className="text-sm text-gray-600">Data-driven advice</div>
                  </div>
                  <div>
                    <div className="text-2xl mb-2">🔒</div>
                    <div className="font-semibold text-gray-900">100% Free</div>
                    <div className="text-sm text-gray-600">No hidden costs</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Decision Wheel Section */}
        <section id="decision-wheel" className="py-8 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Wheel Column */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Decision Wheel</h2>
                    <p className="text-gray-600">Add your options and spin to get AI analysis</p>
                  </div>
                  
                  <Wheel
                    options={options}
                    isSpinning={isSpinning}
                    onSpin={handleSpin}
                    onSpinEnd={spinEnd}
                    result={result}
                    mode={mode}
                    pendingElimination={pendingElimination}
                    soundEnabled={soundEnabled}
                  />
                  
                  {/* Instructions */}
                  <div className="mt-6 text-center">
                    <p className="text-lg text-gray-700 font-medium animate-pulse">
                      Spin the AI decision maker wheel to get started
                    </p>
                  </div>
                </div>
              </div>

              {/* Options Column */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Your Options</h3>
                  
                  {/* Add Option Form */}
                  <div className="mb-6">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Add an option..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                            addOption(e.currentTarget.value.trim())
                            e.currentTarget.value = ''
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          const input = document.querySelector('input[placeholder="Add an option..."]') as HTMLInputElement
                          if (input && input.value.trim()) {
                            addOption(input.value.trim())
                            input.value = ''
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Options List */}
                  <div className="space-y-2 mb-6">
                    {options.map((option) => (
                      <div key={option.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-900">{option.text}</span>
                        <button
                          onClick={() => removeOption(option.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Mode Selector */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Decision Mode</h4>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="mode"
                          value="normal"
                          checked={mode === 'normal'}
                          onChange={(e) => setMode(e.target.value as any)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Normal (Random)</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="mode"
                          value="elimination"
                          checked={mode === 'elimination'}
                          onChange={(e) => setMode(e.target.value as any)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Elimination</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="mode"
                          value="accumulation"
                          checked={mode === 'accumulation'}
                          onChange={(e) => setMode(e.target.value as any)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Accumulation</span>
                      </label>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="space-y-3">
                    <button
                      onClick={handleSpin}
                      disabled={isSpinning || options.length < 2}
                      className="w-full py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSpinning ? 'Spinning...' : 'Spin the Wheel'}
                    </button>
                    
                    {result && (
                      <button
                        onClick={() => setIsContextModalOpen(true)}
                        className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Get AI Analysis
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Simple Social Proof */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Trusted by Decision Makers</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-100">
                <div className="text-3xl font-bold text-blue-600 mb-2">98%</div>
                <div className="text-gray-700 font-medium">User Satisfaction</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-100">
                <div className="text-3xl font-bold text-green-600 mb-2">3s</div>
                <div className="text-gray-700 font-medium">Analysis Time</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-100">
                <div className="text-3xl font-bold text-purple-600 mb-2">50K+</div>
                <div className="text-gray-700 font-medium">Decisions Made</div>
              </div>
            </div>
          </div>
        </section>

        {/* Simple FAQ */}
        <section className="py-12 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How does AI analysis work?</h3>
                <p className="text-gray-600 text-sm">Our AI analyzes your decision using advanced algorithms to provide pros/cons, risk assessments, and actionable insights in just 3 seconds.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Is it really free?</h3>
                <p className="text-gray-600 text-sm">Yes! All features including AI analysis are 100% free with no hidden costs or subscriptions.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Is my data secure?</h3>
                <p className="text-gray-600 text-sm">Absolutely! Your data stays in your browser. No personal information is collected or shared.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">What if I need more analysis?</h3>
                <p className="text-gray-600 text-sm">After spinning, click "Get AI Analysis" for detailed pros/cons, causal chains, and personalized recommendations.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Context Input Modal */}
        <ContextInputModal
          isOpen={isContextModalOpen}
          onClose={() => setIsContextModalOpen(false)}
          onConfirm={(context) => {
            let aiContext = ''
            if (mode === 'normal') {
              aiContext = `AI Decision Maker result: ${result?.text}\n\n${context}`
            } else if (mode === 'elimination') {
              aiContext = `AI Decision Maker elimination mode. Remaining options: ${options.map(o => o.text).join(', ')}.\n\n${context}`
            } else if (mode === 'accumulation') {
              aiContext = context
            }
            navigate('/chat', {
              state: {
                wheelResult: result,
                context: aiContext,
              },
            })
          }}
          result={result}
          mode={mode}
          options={options}
          accumulationCounts={accumulationCounts}
        />
      </div>
    </>
  )
} 